import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LoginForm = ({ onRoleSelect }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');

  const mockCredentials = {
    customer: { email: 'client@plumberpro.fr', password: 'Client2025!', route: '/customer-account-portal' },
    technician: { email: 'technicien@plumberpro.fr', password: 'Tech2025!', route: '/technician-daily-routes' },
    dispatcher: { email: 'dispatch@plumberpro.fr', password: 'Dispatch2025!', route: '/dispatch-control-center', mfa: true },
    trainer: { email: 'formateur@plumberpro.fr', password: 'Trainer2025!', route: '/service-catalog-manager' },
    admin: { email: 'admin@plumberpro.fr', password: 'Admin2025!', route: '/analytics-dashboard', mfa: true }
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData(prev => ({ ...prev, rememberMe: e?.target?.checked }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.email) {
      newErrors.email = 'L\'adresse email est requise';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Format d\'email invalide';
    }

    if (!formData?.password) {
      newErrors.password = 'Le mot de passe est requis';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleGoogleLogin = () => {
    const googleClientId = import.meta.env?.VITE_GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = import.meta.env?.VITE_OAUTH_REDIRECT_URI;
    
    if (!googleClientId || googleClientId === 'your-google-oauth-client-id-here') {
      setErrors({ 
        oauth: 'Configuration Google OAuth manquante. Veuillez configurer VITE_GOOGLE_OAUTH_CLIENT_ID dans le fichier .env' 
      });
      return;
    }

    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${googleClientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `response_type=token&` +
      `scope=email profile&` +
      `state=google`;

    window.location.href = googleAuthUrl;
  };

  const handleFacebookLogin = () => {
    const facebookAppId = import.meta.env?.VITE_FACEBOOK_APP_ID;
    const redirectUri = import.meta.env?.VITE_OAUTH_REDIRECT_URI;
    
    if (!facebookAppId || facebookAppId === 'your-facebook-app-id-here') {
      setErrors({ 
        oauth: 'Configuration Facebook OAuth manquante. Veuillez configurer VITE_FACEBOOK_APP_ID dans le fichier .env' 
      });
      return;
    }

    const facebookAuthUrl = `https://www.facebook.com/v18.0/dialog/oauth?` +
      `client_id=${facebookAppId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `scope=email,public_profile&` +
      `state=facebook`;

    window.location.href = facebookAuthUrl;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);

    setTimeout(() => {
      const matchedRole = Object.entries(mockCredentials)?.find(
        ([_, creds]) => creds?.email === formData?.email && creds?.password === formData?.password
      );

      if (matchedRole) {
        const [role, credentials] = matchedRole;
        
        if (credentials?.mfa && !requiresMFA) {
          setRequiresMFA(true);
          setIsLoading(false);
          return;
        }

        if (requiresMFA && mfaCode !== '123456') {
          setErrors({ mfa: 'Code MFA invalide. Utilisez 123456' });
          setIsLoading(false);
          return;
        }

        localStorage.setItem('userRole', role);
        localStorage.setItem('userEmail', formData?.email);
        localStorage.setItem('isAuthenticated', 'true');
        
        if (onRoleSelect) {
          onRoleSelect(role);
        }
        
        navigate(credentials?.route);
      } else {
        setErrors({ 
          submit: 'Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.' 
        });
      }
      
      setIsLoading(false);
    }, 1500);
  };

  const handleGuestAccess = () => {
    navigate('/service-booking-wizard');
  };

  const handleForgotPassword = () => {
    console.log('Password recovery initiated');
  };

  if (requiresMFA) {
    return (
      <div className="w-full max-w-md">
        <div className="bg-card rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={32} color="var(--color-primary)" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              Authentification à deux facteurs
            </h2>
            <p className="text-sm text-muted-foreground">
              Entrez le code à 6 chiffres envoyé à votre appareil
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Code MFA"
              type="text"
              name="mfaCode"
              placeholder="123456"
              value={mfaCode}
              onChange={(e) => setMfaCode(e?.target?.value)}
              error={errors?.mfa}
              required
              maxLength={6}
              className="text-center text-2xl tracking-widest"
            />

            {errors?.submit && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                <p className="text-sm text-error">{errors?.submit}</p>
              </div>
            )}

            <Button
              type="submit"
              variant="default"
              fullWidth
              loading={isLoading}
              iconName="Lock"
              iconPosition="left"
            >
              Vérifier le code
            </Button>

            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => setRequiresMFA(false)}
            >
              Retour à la connexion
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md">
      <div className="bg-card rounded-lg shadow-md p-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-semibold text-foreground mb-2">
            Connexion à PlumberPro
          </h2>
          <p className="text-sm text-muted-foreground">
            Accédez à votre espace professionnel
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Adresse email"
            type="email"
            name="email"
            placeholder="votre@email.fr"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            required
          />

          <div className="relative">
            <Input
              label="Mot de passe"
              type={showPassword ? 'text' : 'password'}
              name="password"
              placeholder="••••••••"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <Checkbox
              label="Se souvenir de moi"
              checked={formData?.rememberMe}
              onChange={handleCheckboxChange}
            />
            <button
              type="button"
              onClick={handleForgotPassword}
              className="text-sm text-primary hover:underline"
            >
              Mot de passe oublié ?
            </button>
          </div>

          {errors?.submit && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-sm text-error">{errors?.submit}</p>
            </div>
          )}

          {errors?.oauth && (
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-3 flex items-start gap-2">
              <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
              <p className="text-sm text-warning">{errors?.oauth}</p>
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            fullWidth
            loading={isLoading}
            iconName="LogIn"
            iconPosition="left"
          >
            Se connecter
          </Button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Ou continuer avec</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Google
            </button>

            <button
              type="button"
              onClick={handleFacebookLogin}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors font-medium text-sm"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Facebook
            </button>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Ou</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            fullWidth
            onClick={handleGuestAccess}
            iconName="UserPlus"
            iconPosition="left"
          >
            Réserver sans compte
          </Button>
        </form>

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={18} color="#3B82F6" className="flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-700">
              <p className="font-semibold mb-1">Configuration OAuth requise</p>
              <p>Pour activer la connexion Google et Facebook, configurez les clés suivantes dans votre fichier .env :</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>VITE_GOOGLE_OAUTH_CLIENT_ID</li>
                <li>VITE_FACEBOOK_APP_ID</li>
                <li>VITE_OAUTH_REDIRECT_URI</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;